import type { BenchmarkReturns } from '../api/client'
import type { PickBar } from './pickPerformance'
import type { Timeframe } from './timeframe'

const WINDOW_BY_TF: Partial<Record<Timeframe, keyof BenchmarkReturns['windows']>> = {
  DAY: 'day', '1W': '1w', '1M': '1m', '3M': '3m',
  // ALL deliberately unmapped: there is no honest all-time SPY baseline.
}

export function spyReturnForTimeframe(
  tf: Timeframe,
  data: BenchmarkReturns | undefined,
): number | null {
  const key = WINDOW_BY_TF[tf]
  if (!key || !data) return null
  return data.windows[key]?.return_pct ?? null
}

/**
 * IMPORTANT 4: the `day` window's daily-bar `end` date is often the PRIOR
 * session while the market is still open (the current day's daily bar
 * hasn't formed yet) — so the DAY-window SPY return can be yesterday's
 * number while the picks it's charted against are live intraday. Callers
 * label rather than null this: compare the returned date to the viewer's
 * local calendar date and swap "today" for the explicit end date when they
 * differ. Returns null when the day window/end is unavailable.
 */
export function spyDayEndDate(data: BenchmarkReturns | undefined): string | null {
  return data?.windows.day?.end ?? null
}

/**
 * "Beating SPY: N of M" (spec §5 tile ④). DAY compares each pick's day
 * return to SPY's day return; longer windows use the per-pick since-entry
 * excess the scorecard already provides (each pick vs SPY over ITS OWN
 * holding window — a single SPY line is meaningless across entry dates).
 * Returns null when no honest comparison exists.
 */
export function beatingCount(
  tf: Timeframe,
  bars: PickBar[],
  spyDayPct: number | null,
): { beating: number; of: number } | null {
  if (bars.length === 0) return null
  if (tf === 'DAY') {
    if (spyDayPct === null) return null
    return { beating: bars.filter((b) => b.returnPct > spyDayPct).length, of: bars.length }
  }
  const benchmarked = bars.filter((b) => b.excessSinceEntryPct !== null)
  if (benchmarked.length === 0) return null
  return {
    beating: benchmarked.filter((b) => (b.excessSinceEntryPct as number) > 0).length,
    of: benchmarked.length,
  }
}
